import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { skusApi, requisitionsApi, reportsApi } from '../api';
import StatCard from '../components/StatCard';
import PageHeader from '../components/PageHeader';
import CoolingCountdownTile from '../components/CoolingCountdownTile';
import { Loader2, TrendingUp } from 'lucide-react';

export default function EBODashboard() {
  const { user, theme } = useAuth();
  const navigate = useNavigate();
  const [reqs, setReqs] = useState([]);
  const [eligible, setEligible] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sVsT, setSVsT] = useState(null);
  const [skuList, setSkuList] = useState([]);
  const [cooldownMap, setCooldownMap] = useState({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [rs, skus, cooldown] = await Promise.all([
          requisitionsApi.list(), skusApi.list(), requisitionsApi.cooldown(),
        ]);
        setReqs(rs);
        setSkuList(skus);
        setCooldownMap(cooldown || {});
        const eboEligible = skus.filter(s => s.status === 'Active' && s.channel?.includes('EBO'));
        setEligible(eboEligible.length - Object.keys(cooldown).length);
      } finally { setLoading(false); }
    })();
    // Best-effort sales-vs-tester tile — falls back silently if the endpoint
    // isn't visible to EBOs (older permission matrix).
    reportsApi.testerToSales(user?.branchCode || 'all', '6')
      .then(setSVsT).catch(() => setSVsT(null));
  }, [user?.branchCode]);

  const flat = reqs.flatMap(r => r.lines);
  const awaiting = flat.filter(l => l.lineStatus === 'Requested').length;
  const packed = flat.filter(l => l.lineStatus === 'Fulfilled').length;
  const inTransit = flat.filter(l => l.lineStatus === 'Shipped').length;
  const delivered = flat.filter(l => l.lineStatus === 'Delivered').length;

  if (loading) return <div className="p-10 text-center text-[#7a6f5c]"><Loader2 className="animate-spin inline" /></div>;

  return (
    <div>
      <PageHeader
        title={<span>Welcome, <span style={{ color: theme.primary }}>{user?.name?.split(' ')[0]}</span></span>}
        subtitle={<span className="font-mono text-[12px] tracking-wide">{user?.branchCode}</span>}
      />

      <div className="flex flex-wrap gap-4">
        <StatCard label="Awaiting warehouse" value={awaiting} valueColor="#232323" />
        <StatCard label="Packed, awaiting courier" value={packed} valueColor="#B78140" />
        <StatCard label="In transit" value={inTransit} valueColor={theme.primary} />
        <StatCard label="Delivered" value={delivered} valueColor="#232323" />
        <StatCard label="SKUs eligible today" value={eligible} valueColor="#4D7C5F" />
      </div>

      <p className="mt-6 text-[13px] text-[#7a6f5c] max-w-[820px] leading-relaxed">
        Each SKU is tracked independently for your branch — one item locked never blocks another. Head to
        <button onClick={() => navigate('/app/raise')} className="mx-1 underline font-medium" style={{ color: theme.primary }}>Raise Requisition</button>
        to see live eligibility.
      </p>

      <CoolingCountdownTile cooldown={cooldownMap} skus={skuList} theme={theme} />

      {/* iter37 — Sales vs Tester tile (branch-scoped) */}
      {sVsT?.rows?.length > 0 && (
        <div className="fm-card p-5 mt-6" data-testid="ebo-sales-vs-tester">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={16} style={{ color: theme.primary }} />
            <h3 className="text-[15px] font-medium">Sales vs testers issued · last 6 months</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11.5px] text-[#7a6f5c] border-b border-[#EFEADC]">
                  <th className="py-2 pr-4 font-medium">SKU</th>
                  <th className="py-2 pr-4 font-medium">Product</th>
                  <th className="py-2 pr-4 font-medium text-right">Testers issued</th>
                  <th className="py-2 pr-4 font-medium text-right">Pieces sold</th>
                  <th className="py-2 pr-4 font-medium text-right">Ratio</th>
                </tr>
              </thead>
              <tbody>
                {(sVsT.rows || []).slice(0, 10).map((row, i) => (
                  <tr key={i} className="border-t border-[#EFEADC]">
                    <td className="py-2 pr-4 font-mono text-[12px]">{row.sku}</td>
                    <td className="py-2 pr-4">{row.product}</td>
                    <td className="py-2 pr-4 text-right">{row.testers}</td>
                    <td className="py-2 pr-4 text-right">{row.sales}</td>
                    <td className="py-2 pr-4 text-right font-semibold" style={{ color: theme.primary }}>
                      {row.testers > 0 ? (row.sales / row.testers).toFixed(1) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-[#7a6f5c] mt-3">
            Higher ratio = fewer testers driving more sales. Investigate SKUs below ratio 1.0.
          </div>
        </div>
      )}
    </div>
  );
}
